"use client";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { USERS_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveUser from "@/hooks/requests/user/user-remove-user";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import IUser from "@/interfaces/user.interface";
import { UsersMessages } from "@/messages/users/user.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface UserPopoverProps {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: UsersMessages;
  selectedUser: IUser;
}

export function UserPopover({
  isOpen,
  anchorEl,
  onClose,
  messages,
  selectedUser,
}: UserPopoverProps) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<IUser>();

  const { removeUser, isLoading: isLoadingRemoveUser } = useRemoveUser();
  const { mutate } = useMutate();

  const hasNoPermissionToEdit = useHasNoPermission(USERS_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(USERS_PERMISSIONS.DELETE);

  const {
    deleteUserDialogTitle,
    deleteUserSucess: successMessage,
    deleteUserDialogContent,
    deleteUserDialogCancel,
    deleteUserDialogConfirm,
    removePopover,
    updatePopover,
  } = messages;

  const handleOnEdit = () => {
    router(ROUTE_URLS.updateUser(lang, selectedUser.id));
  };

  const handleConfirmDelete = async (user: IUser) => {
    await removeUser({
      id: user.id,
      onSuccess: () => mutate(SWR_KEYS.getUsers),
    });
    handleClose();
  };

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={handleOnEdit}
        title={updatePopover}
      />
      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selectedUser)}
        title={removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => handleConfirmDelete(selectedUser)}
        titleText={deleteUserDialogTitle}
        descriptionText={deleteUserDialogContent}
        cancelText={deleteUserDialogCancel}
        confirmText={deleteUserDialogConfirm}
        isLoading={isLoadingRemoveUser}
      />
    </Menu>
  );
}
